import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { PartyPopper, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, photoUrl } from "@/lib/api";
import MilestoneDialog from "@/pages/MilestoneDialog";
import InviteNudge from "@/pages/InviteNudge";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function burst() {
  const opts = { particleCount: 90, spread: 75, zIndex: 9999 };
  confetti({ ...opts, origin: { x: 0.2, y: 0.6 }, angle: 60 });
  confetti({ ...opts, origin: { x: 0.8, y: 0.6 }, angle: 120 });
  setTimeout(() => confetti({ particleCount: 140, spread: 110, origin: { y: 0.35 }, zIndex: 9999 }), 350);
}

export default function FirstSaleDialog() {
  const [sale, setSale] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const navigate = useNavigate();

  // First sale wins; milestones queue behind it so dialogs never stack.
  const checkMilestone = () => api.get("/milestones").then(({ data }) => {
    if (data?.celebrate) { setMilestone(data.celebrate); setTimeout(burst, 250); }
  }).catch(() => {});

  useEffect(() => {
    api.get("/first-sale").then(({ data }) => {
      if (data?.sale) { setSale(data.sale); setTimeout(burst, 250); }
      else checkMilestone();
    }).catch(() => {});
  }, []);

  const close = () => {
    setSale(null);
    api.post("/first-sale/ack").catch(() => {});
    setTimeout(checkMilestone, 400);
  };

  const closeMilestone = () => {
    api.post("/milestones/ack", { n: milestone }).catch(() => {});
    setMilestone(null);
  };

  const share = async () => {
    const text = `I just made my first sale on eBay — "${sale.title}" for ${money(sale.sold_price)}! Listed with SnapList.`;
    try {
      if (navigator.share) await navigator.share({ title: "My first sale!", text, url: sale.listing_url || undefined });
      else { await navigator.clipboard.writeText(sale.listing_url ? `${text} ${sale.listing_url}` : text); toast.success("Copied — paste it anywhere to share"); }
    } catch { /* user cancelled */ }
  };

  const listAnother = () => { close(); navigate("/"); };

  if (milestone) return <MilestoneDialog n={milestone} onClose={closeMilestone} />;

  return (
    <Dialog open={!!sale} onOpenChange={(open) => { if (!open) close(); }}>
      {sale ? (
        <DialogContent className="max-w-sm overflow-hidden rounded-2xl p-0" data-testid="first-sale-dialog">
          <div className="relative bg-gradient-to-br from-amber-100 via-white to-emerald-50 px-6 pb-4 pt-6 text-center">
            <PartyPopper className="mx-auto h-8 w-8 text-amber-500" />
            <DialogHeader className="mt-2 space-y-1 text-center sm:text-center">
              <DialogTitle className="text-3xl font-bold tracking-tight">First sale!</DialogTitle>
              <DialogDescription>Your first item just sold on eBay. Nice work.</DialogDescription>
            </DialogHeader>
          </div>
          <div className="space-y-4 px-6 pb-6">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="first-sale-item">
              {sale.photo_id ? (
                <img src={photoUrl(sale.photo_id, "thumb")} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" data-testid="first-sale-photo" />
              ) : <div className="h-16 w-16 shrink-0 rounded-lg bg-slate-100" />}
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-medium text-slate-900" data-testid="first-sale-title">{sale.title}</p>
                <p className="mt-0.5 text-xl font-bold text-emerald-600" data-testid="first-sale-price">{money(sale.sold_price)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={share} className="h-11 rounded-full" data-testid="first-sale-share">
                <Share2 className="mr-2 h-4 w-4" /> Share
              </Button>
              <Button onClick={listAnother} className="h-11 rounded-full bg-[#0053DA] hover:bg-[#0043B0]" data-testid="first-sale-list-another">
                <Plus className="mr-2 h-4 w-4" /> List another
              </Button>
            </div>
            <InviteNudge />
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
